<template>
  <div class="bg-white rounded-lg shadow-sm border border-gray-200">
    <div class="px-6 py-4 border-b border-gray-200">
      <h2 class="text-lg font-semibold text-gray-900">
        {{ device ? 'Device Details' : 'Select Device' }}
      </h2>
      <p v-if="device" class="text-sm text-gray-500 mt-1">
        {{ device.deviceId }}
      </p>
    </div>

    <!-- No Device Selected -->
    <div v-if="!device" class="p-12 text-center">
      <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.121 2.122" />
      </svg>
      <h3 class="mt-2 text-sm font-medium text-gray-900">No device selected</h3>
      <p class="mt-1 text-sm text-gray-500">
        Select a device from the list to view its shadow state and details.
      </p>
    </div>

    <!-- Device Details -->
    <div v-else class="p-6 space-y-6">
      <!-- Device Status -->
      <div class="flex items-center justify-between">
        <div class="flex items-center space-x-2">
          <span 
            class="status-indicator text-sm"
            :class="statusClasses(device.status)"
          >
            <span class="pulse-dot mr-1"></span>
            {{ device.status.toUpperCase() }}
          </span>
          <span v-if="isInSync" class="inline-flex items-center px-2 py-0.5 rounded bg-blue-100 text-blue-600 text-xs font-semibold">
            <svg class="w-4 h-4 mr-1 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/></svg>
            In Sync
          </span>
        </div>
        <div class="text-right text-sm text-gray-500">
          <div>Version: {{ device.shadow?.version || 0 }}</div>
          <div>{{ formatLastSeen(device.lastSeen) }}</div>
        </div>
      </div>

      <!-- Quick Stats -->
      <div class="grid grid-cols-2 gap-4 py-4 border-t border-b border-gray-200">
        <div>
          <div class="text-sm font-medium text-gray-500">Device Type</div>
          <div class="mt-1 text-sm text-gray-900">{{ device.deviceType || 'ESP32' }}</div>
        </div>
        <div>
          <div class="text-sm font-medium text-gray-500">IP Address</div>
          <div class="mt-1 text-sm text-gray-900">
            {{ device.network?.ipAddress || 'Unknown' }}
          </div>
        </div>
      </div>

      <!-- Shadow State Tabs -->
      <div>
        <div class="border-b border-gray-200">
          <nav class="-mb-px flex space-x-8">
            <button
              v-for="tab in shadowTabs"
              :key="tab.key"
              @click="activeTab = tab.key"
              :class="[
                activeTab === tab.key
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300',
                'whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm'
              ]"
            >
              {{ tab.label }}
              <span 
                class="ml-2 py-0.5 px-2 rounded-full text-xs"
                :class="activeTab === tab.key ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-600'"
              >
                {{ Object.keys(tab.data).length }}
              </span>
            </button>
          </nav>
        </div>

        <!-- Tab Content -->
        <div class="mt-4">
          <!-- JSON Viewer -->
          <JsonViewer 
            :data="currentTabData" 
            :editable="activeTab === 'desired'"
            @update="handleUpdateDesired"
          />
        </div>
      </div>

      <!-- Actions -->
      <div class="flex items-center justify-between pt-4 border-t border-gray-200">
        <button
          @click="$emit('update-desired', device.deviceId, {})"
          class="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
        >
          Clear Desired
        </button>
        
        <div class="flex space-x-3">
          <button
            @click="refreshDevice"
            class="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Refresh
          </button>
          <button
            @click="syncDevice"
            class="px-3 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700"
          >
            Sync Device
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import JsonViewer from './JsonViewer.vue'

const props = defineProps({
  device: {
    type: Object,
    default: null
  }
})

const emit = defineEmits(['update-desired'])

const activeTab = ref('reported')

const shadowTabs = computed(() => {
  if (!props.device?.shadow) return []
  
  return [
    {
      key: 'reported',
      label: 'Reported State',
      data: props.device.shadow.reported || {}
    },
    {
      key: 'desired',
      label: 'Desired State',
      data: props.device.shadow.desired || {}
    }
  ]
})

const currentTabData = computed(() => {
  const tab = shadowTabs.value.find(t => t.key === activeTab.value)
  return tab ? tab.data : {}
})


// Deep comparison for sync detection (handles nested objects)
function deepEqual(a, b) {
  if (a === b) return true
  if (typeof a !== typeof b) return false
  if (typeof a !== 'object' || a === null || b === null) return false
  const aKeys = Object.keys(a)
  for (const key of aKeys) {
    if (!(key in b)) return false
    if (!deepEqual(a[key], b[key])) return false
  }
  return true
}

const isInSync = computed(() => {
  if (!props.device?.shadow) return false
  const reported = props.device.shadow.reported || {}
  const desired = props.device.shadow.desired || {}
  return deepEqual(desired, reported)
})

const statusClasses = (status) => {
  switch (status) {
    case 'online':
      return 'status-online'
    case 'offline':
      return 'status-offline'
    case 'reconnecting':
      return 'status-reconnecting'
    default:
      return 'bg-gray-100 text-gray-800'
  }
}

const formatLastSeen = (lastSeen) => {
  if (!lastSeen) return 'Never seen'
  
  const now = new Date()
  const seen = new Date(lastSeen)
  const diffMs = now - seen
  const diffSecs = Math.floor(diffMs / 1000)
  const diffMins = Math.floor(diffSecs / 60)
  const diffHours = Math.floor(diffMins / 60)
  
  if (diffSecs < 30) return 'Just now'
  if (diffSecs < 60) return `${diffSecs}s ago`
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  
  return seen.toLocaleDateString()
}

const handleUpdateDesired = (newData) => {
  if (props.device) {
    emit('update-desired', props.device.deviceId, newData)
  }
}

const refreshDevice = () => {
  // Emit refresh event or call store method
  console.log('Refreshing device:', props.device.deviceId)
}

const syncDevice = () => {
  // Emit sync event
  console.log('Syncing device:', props.device.deviceId)
}

// Only reset to reported tab when deviceId changes
let lastDeviceId = null
watch(
  () => props.device?.deviceId,
  (newId, oldId) => {
    if (newId && newId !== oldId) {
      activeTab.value = 'reported'
    }
    lastDeviceId = newId
  }
)
</script>